import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
const BookOnline = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preSelectedCenter = searchParams.get('center');

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    therapies: [],
    center: preSelectedCenter || '',
    branch: '',
    appointmentDate: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set pre-selected center from URL
  useEffect(() => {
    if (preSelectedCenter) {
      setFormData(prevData => ({
        ...prevData,
        center: preSelectedCenter
      }));
    }
  }, [preSelectedCenter]);

  const therapyOptions = [
    'Occupational Therapy',
    'Speech Therapy',
    'Special Education',
    'Physiotherapy'
  ];

  const centerOptions = [
    'Muzaffarpur',
    'Sitamarhi'
  ];

  const branchOptions = {
    'Muzaffarpur': [
      'Power House Chowk',
      'Akharaghat'
    ],
    'Sitamarhi': [
      'Dumra'
    ]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Name validation: Only allow alphabets and spaces
    if (name === 'name') {
      if (value === '' || /^[a-zA-Z\s]+$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
      return;
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTherapyChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        therapies: [...formData.therapies, value]
      });
    } else {
      setFormData({
        ...formData,
        therapies: formData.therapies.filter(therapy => therapy !== value)
      });
    }
  };

  const handleCenterChange = (e) => {
    setFormData({
      ...formData,
      center: e.target.value,
      branch: '' // Reset branch when center changes
    });
  };

  const handleBranchChange = (e) => {
    setFormData({
      ...formData,
      branch: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.address || !formData.phone || formData.therapies.length === 0 || !formData.center || !formData.branch || !formData.appointmentDate) {
      toast.error('Please fill in all fields');
      return;
    }

    // Phone Validation
    const phoneRegex = /^(\+\d{2})?\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid phone number (10 digits or +91 followed by 10 digits, e.g., 9876543210 or +919876543210)');
      return;
    }

    try {
      setIsSubmitting(true);
      // Send data to backend
      const response = await fetch('https://kidsworkrehab.onrender.com/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Store last booking details for the success popup
        setLastBooking({
          name: formData.name,
          phone: formData.phone,
          therapies: [...formData.therapies],
          center: formData.center,
          branch: formData.branch,
          appointmentDate: formData.appointmentDate
        });
        setShowSuccess(true);

        // Reset form (keep pre-selected center if it came from URL)
        setFormData({
          name: '',
          address: '',
          phone: '',
          therapies: [],
          center: preSelectedCenter || '',
          branch: '',
          appointmentDate: ''
        });
      } else {
        toast.error(`Error: ${result.message || 'Failed to submit booking. Please try again.'}`);
      }
    } catch (error) {
      toast.error('Failed to submit booking. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans text-gray-700 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="bg-indigo-700 py-20 px-5 text-center text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-900/50 to-violet-900/50"></div>
        <div className="absolute -bottom-10 -right-10 text-white/5 rotate-12">
          <Calendar size={300} strokeWidth={1} />
        </div>
        <div className="relative z-10 max-w-[800px] mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 ">Book Online</h1>
          <p className="text-xl text-indigo-100 font-light">
            Fill out the form below to schedule your appointment with our specialists.
          </p>
        </div>
      </div>

      <div className="max-w-[700px] mx-auto -mt-10 px-5 relative z-20 pb-20">
        <form className="bg-white p-8 md:p-10 rounded-[20px] shadow-xl border border-gray-100" onSubmit={handleSubmit}>

          <div className="mb-6">
            <label className="block text-indigo-700 font-semibold mb-2">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
              placeholder="Enter your name"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-indigo-700 font-semibold mb-2">Address *</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none resize-y min-h-[100px]"
              placeholder="Enter your address"
              rows="3"
              required
            />
          </div>


          <div className="mb-6">
            <label className="block text-indigo-700 font-semibold mb-2">
              Phone Number *
            </label>

            <div className="flex">
              {/* Country Code */}
              <span className="flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-200 bg-gray-100 text-gray-700 font-medium">
                +91
              </span>

              {/* Phone Number Input */}
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // only numbers
                  if (value.length <= 10) {
                    setFormData({ ...formData, phone: value });
                  }
                }}
                className="w-full px-4 py-3 rounded-r-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
                placeholder="Enter 10-digit number"
                required
                pattern="[0-9]{10}"
                maxLength={10}
              />
            </div>

          </div>

          <div className="mb-6">
            <label className="block text-indigo-700 font-semibold mb-2">Appointment Date *</label>
            <input
              type="date"
              name="appointmentDate"
              value={formData.appointmentDate}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
              required
            />
          </div>


          <div className="mb-6">
            <label className="block text-indigo-700 font-semibold mb-2">Therapy * (Select one or more)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {therapyOptions.map((therapy) => (
                <label key={therapy} className={`flex items-center cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 ${formData.therapies.includes(therapy) ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-gray-50 border-transparent hover:bg-gray-100'}`}>
                  <input
                    type="checkbox"
                    value={therapy}
                    checked={formData.therapies.includes(therapy)}
                    onChange={handleTherapyChange}
                    className="w-5 h-5 mr-3 accent-indigo-600 rounded"
                  />
                  <span className="font-medium">{therapy}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-indigo-700 font-semibold mb-2">
              City * {preSelectedCenter && <span className="text-gray-400 text-sm font-normal italic">(Pre-selected)</span>}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {centerOptions.map((center) => (
                <label key={center} className={`flex items-center cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 ${formData.center === center ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-gray-50 border-transparent hover:bg-gray-100'} ${preSelectedCenter ? 'opacity-75' : ''}`}>
                  <input
                    type="radio"
                    name="center"
                    value={center}
                    checked={formData.center === center}
                    onChange={handleCenterChange}
                    className="w-5 h-5 mr-3 accent-indigo-600"
                    disabled={!!preSelectedCenter}
                    required
                  />
                  <span className="font-medium">{center}</span>
                </label>
              ))}
            </div>
          </div>

          {formData.center && branchOptions[formData.center] && (
            <div className="mb-8 animate-fade-in">
              <label className="block text-indigo-700 font-semibold mb-2">
                Branch *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {branchOptions[formData.center].map((branch) => (
                  <label key={branch} className={`flex items-center cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 ${formData.branch === branch ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-gray-50 border-transparent hover:bg-gray-100'}`}>
                    <input
                      type="radio"
                      name="branch"
                      value={branch}
                      checked={formData.branch === branch}
                      onChange={handleBranchChange}
                      className="w-5 h-5 mr-3 accent-indigo-600"
                      required
                    />
                    <span className="font-medium">{branch}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Booking Request'
            )}
          </button>
        </form>

        <div className="text-center mt-8 bg-white/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-100">
          <p className="text-gray-600">For immediate assistance, call us at <a href="tel:7727043497" className="text-green-700 font-bold hover:underline">7727043497</a>, <a href="tel:8797681753" className="text-green-700 font-bold hover:underline">8797681753</a></p>
        </div>
      </div>

      {showSuccess && lastBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5 animate-fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-[480px] w-full text-center shadow-2xl relative overflow-hidden animate-scale-up">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-emerald-500"></div>

            <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} strokeWidth={3} />
            </div>

            <h2 className="text-3xl font-bold text-gray-800 mb-2 ">Booking Successful!</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Thank you, <strong>{lastBooking.name}</strong>. Your booking request has been received. We will get back to you soon.
            </p>

            <div className="bg-gray-50 rounded-xl p-5 mb-8 text-left space-y-3 border border-gray-100">
              <div className="flex justify-between items-start border-b border-gray-200 pb-2">
                <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider">Appointment Date</span>
                <span className="text-gray-800 font-medium">{lastBooking.appointmentDate ? new Date(lastBooking.appointmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span>
              </div>
              <div className="flex justify-between items-start border-b border-gray-200 pb-2">
                <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider">Phone</span>
                <span className="text-gray-800 font-medium">{lastBooking.phone}</span>
              </div>
              <div className="flex justify-between items-start border-b border-gray-200 pb-2">
                <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider">City</span>
                <span className="text-gray-800 font-medium">{lastBooking.center}</span>
              </div>
              <div className="flex justify-between items-start border-b border-gray-200 pb-2">
                <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider">Branch</span>
                <span className="text-gray-800 font-medium text-right max-w-[60%]">{lastBooking.branch}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider">Therapies</span>
                <div className="text-gray-800 font-medium text-right flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {lastBooking.therapies.map((t, i) => (
                    <span key={i} className="bg-white px-2 py-0.5 rounded border border-gray-200 text-xs">{t}</span>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold hover:bg-gray-800 transition-colors shadow-lg"
              onClick={() => setShowSuccess(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookOnline;
